/**
 * SpecialistAkkoordExpander — inline form binnen een bundle-item-tile.
 *
 * Albert (of een andere wasserij-specialist) kan een gemeld risico afdoen
 * zonder klant-mail. Audit-trail vereist een verplichte reden (≥ 10 chars)
 * + de User die het besluit nam (default = current user als die admin/
 * medewerker is, anders Albert via display_name match).
 *
 * Geen klant-snapshot, geen mail-trigger — puur status-flip + reden.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Select, Spinner } from '../ui';
import { useAuthStore } from '../../stores';
import {
  fetchSpecialists,
  markSpecialistAkkoord,
  type Specialist,
} from '../../lib/risico';

interface Props {
  wasserijItemId: string;
  kleedNaam: string;
  onSuccess: (wasserijItemId: string) => void;
  onCancel: () => void;
}

const REDEN_MIN_LENGTH = 10;

export function SpecialistAkkoordExpander({
  wasserijItemId,
  kleedNaam,
  onSuccess,
  onCancel,
}: Props) {
  const currentEmail = useAuthStore((s) => s.user?.email)?.toLowerCase();
  const [specialists, setSpecialists] = useState<Specialist[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [specialistUserId, setSpecialistUserId] = useState('');
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSpecialists()
      .then((list) => {
        if (cancelled) return;
        setSpecialists(list);

        // Default: current user als die in lijst zit, anders Albert (display_name match), anders eerste.
        const byEmail = currentEmail
          ? list.find((s) => s.email.toLowerCase() === currentEmail)
          : undefined;
        const albert = list.find((s) =>
          s.displayName.toLowerCase().includes('albert')
        );
        const def = byEmail ?? albert ?? list[0];
        if (def) setSpecialistUserId(def.userId);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Specialist-lijst ophalen mislukt');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentEmail]);

  const redenTrimmed = reden.trim();
  const canSubmit =
    !!specialistUserId && redenTrimmed.length >= REDEN_MIN_LENGTH && !submitting;

  const options = useMemo(
    () =>
      (specialists ?? []).map((s) => ({
        value: s.userId,
        label: s.displayName,
      })),
    [specialists]
  );

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const result = await markSpecialistAkkoord(wasserijItemId, specialistUserId, redenTrimmed);
    setSubmitting(false);
    if (result.success) {
      onSuccess(wasserijItemId);
      return;
    }
    setSubmitError(result.error || 'Specialist-akkoord mislukt');
  };

  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-3">
      <div className="text-xs font-medium text-emerald-900">
        Specialist akkoord — {kleedNaam} (geen klant-mail)
      </div>

      {loadError && (
        <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
          {loadError}
        </div>
      )}

      {specialists === null && !loadError ? (
        <div className="flex items-center gap-2 text-xs text-emerald-800">
          <Spinner /> Specialisten laden…
        </div>
      ) : (
        <>
          <div>
            <label className="block text-xs font-medium text-emerald-900 mb-1">
              Wie heeft besloten?
            </label>
            <Select
              options={options}
              value={specialistUserId}
              onChange={(e) => setSpecialistUserId(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-emerald-900 mb-1">
              Reden (minimaal {REDEN_MIN_LENGTH} tekens, intern audit-trail)
            </label>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={3}
              placeholder="Bijv. 'Scheurtje is jaar oud, kleed stevig genoeg om voorzichtig te wassen.'"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-emerald-800">
              {redenTrimmed.length}/{REDEN_MIN_LENGTH}+ tekens
            </p>
          </div>
        </>
      )}

      {submitError && (
        <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
          {submitError}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Vastleggen…' : 'Bevestig specialist-akkoord'}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="text-xs text-gray-600 hover:underline disabled:opacity-50"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
}
